import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const hasToken = token.length > 0;

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 10) {
      setErr("Passphrase must be at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passphrases do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      // brief pause so the operator can read the success state, then bounce to login
      setTimeout(() => nav("/admin/login", { replace: true }), 1800);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Reset link is invalid or has expired.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 admin-grid-bg p-4">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <div className="font-display text-4xl font-black text-white tracking-tight">
            IL<span className="text-amber-500">/</span>NEWSROOM
          </div>
        </div>

        {!hasToken ? (
          <div
            data-testid="reset-password-missing-token-card"
            className="border border-zinc-800 bg-zinc-900/50 backdrop-blur p-6 rounded-sm space-y-4"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-amber-500" size={22} />
              <div className="font-display text-lg font-bold text-white">No reset token</div>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              This page needs a single-use token from the recovery email. Open the link directly from the email, or request a new one.
            </p>
            <Link
              data-testid="reset-password-request-new-link"
              to="/admin/forgot-password"
              className="block w-full text-center bg-white text-zinc-950 hover:bg-zinc-200 transition-colors py-2.5 rounded-sm text-sm font-semibold"
            >
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div
            data-testid="reset-password-success-card"
            className="border border-zinc-800 bg-zinc-900/50 backdrop-blur p-6 rounded-sm space-y-4"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-emerald-500" size={22} />
              <div className="font-display text-lg font-bold text-white">Passphrase updated</div>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Your new passphrase is active. Redirecting you to the login screen…
            </p>
          </div>
        ) : (
          <form
            data-testid="reset-password-form"
            onSubmit={submit}
            className="border border-zinc-800 bg-zinc-900/50 backdrop-blur p-6 rounded-sm space-y-4"
          >
            <div>
              <h1 className="font-display text-xl font-bold text-white mb-1">Choose a new passphrase</h1>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Minimum 10 characters. The recovery link can only be used once.
              </p>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">New Passphrase</label>
              <input
                data-testid="reset-password-new-input"
                type="password"
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="admin-input w-full bg-transparent border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100"
                placeholder="•••••••••••"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Confirm Passphrase</label>
              <input
                data-testid="reset-password-confirm-input"
                type="password"
                required
                minLength={10}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="admin-input w-full bg-transparent border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100"
                placeholder="•••••••••••"
              />
            </div>

            {err && (
              <div
                data-testid="reset-password-error"
                className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono"
              >
                {err}
              </div>
            )}

            <button
              data-testid="reset-password-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 transition-colors py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="animate-spin" size={14} />}
              Set new passphrase
            </button>

            <div className="pt-2 border-t border-zinc-800 text-center">
              <Link
                data-testid="reset-password-cancel-link"
                to="/admin/login"
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors font-mono"
              >
                ← Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
